"use client";

import { useEffect, useId, useRef, useState } from "react";
import { Badge } from "./badge";
import { Feld } from "./formular/feld";
import { Mehrzeiler } from "./formular/mehrzeiler";
import { Schalter } from "./formular/schalter";
import { Trefferfeld, type Treffer } from "./formular/trefferfeld";
import { initialen } from "./formular/auswahl-konfiguration";
import { Knopf } from "./knopf";
import { KleinesModal, type KleinesModalErgebnis } from "./modal";

/*
 * Informieren-Dialog (Musterkatalog M-194 I-1, Design-Guide `informieren-dialog.md`, Paket I1): globaler Dialog, mit dem eine Person
 * jemanden aktiv auf ein Objekt hinweist. Fenster D6 (`KleinesModal` Größe M): Empfänger-Auswahl (Chips, vorbelegt), Wege als Zeilen
 * mit Symbol (Hinweis im Portal immer, E-Mail als Schalter, Teams gesperrt mit Satz), optionale Nachricht, Fuß mit „Link kopieren“.
 * Der Baustein kennt keine Server-Action: Suche und Senden kommen als Funktionen des Aufrufers (der Aufrufer prüft mit derselben
 * Prüfung, die die Action anwendet, Anbieten = Erlauben, r109/r110). Ablehnung und Fehlschlag stehen als Satz im offenen Fenster
 * (r98, r105), nie als Code; die leere Auswahl ist behebbar und steht am Feld (r100).
 */

export interface InformierenPerson {
  id: string;
  name: string;
  /** Stelle oder Abteilung; nie die E-Mail-Adresse. */
  untertitel?: string;
}

export type InformierenErgebnis = { ok: true } | { ok: false; meldung: string };

export interface InformierenDialogProps {
  offen: boolean;
  beimSchliessen: () => void;
  /** Unterzeile des Titels: „Entscheidung „…“, Frist 20.11.2026“. */
  beschreibung: string;
  /** Satz am Auswahlfeld: „Zur Auswahl stehen Personen, die diese Entscheidung lesen dürfen. …“ */
  personenHilfe: string;
  vorbelegt: InformierenPerson[];
  /** Name der sendenden Person (Satz „Antworten gehen an dich (Name)“). */
  senderName: string;
  /** Vollständige Adresse des Objekts für die sendende Person (zum Kopieren). */
  linkUrl: string;
  /** „Der Link öffnet die Entscheidung nur für Personen, die sie lesen dürfen.“ */
  linkSatz: string;
  /** Quelle der Auswahl; `null` = die Suche ist gescheitert (Fehlschlag-Satz am Feld). */
  suche: (anfrage: string) => Promise<InformierenPerson[] | null>;
  /** Der Vorgang; `anfrageId` ist je geöffnetem Dialog stabil (Idempotenz: dieselbe Anfrage sendet höchstens einmal). */
  senden: (e: { empfaengerIds: string[]; mail: boolean; nachricht: string; anfrageId: string }) => Promise<InformierenErgebnis>;
  /** Nach erfolgreichem Senden (das Fenster schließt); der Aufrufer sagt es an der Seite (kein Toast). */
  beimErfolg?: () => void;
}

const NACHRICHT_MAX = 1000;

// Lucide-Pfade inline (R-F: das Bausteinpaket bindet `lucide-react` nicht selbst ein), `currentColor`.
function Symbol({ children, klasse = "shrink-0 size-4" }: { children: React.ReactNode; klasse?: string }) {
  return (
    <svg className={klasse} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {children}
    </svg>
  );
}
const GlockeSymbol = () => (
  <Symbol>
    <path d="M10.268 21a2 2 0 0 0 3.464 0" />
    <path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326" />
  </Symbol>
);
const MailSymbol = () => (
  <Symbol>
    <rect width="20" height="16" x="2" y="4" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </Symbol>
);
const PersonenSymbol = () => (
  <Symbol>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </Symbol>
);
const SchlossSymbol = () => (
  <Symbol klasse="shrink-0 size-3">
    <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </Symbol>
);
const KopierSymbol = () => (
  <Symbol klasse="shrink-0 size-3.5">
    <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
  </Symbol>
);
const EntfernenSymbol = () => (
  <Symbol klasse="shrink-0 size-3">
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </Symbol>
);

const WEGE_LISTE = "flex flex-col bg-layer border border-layer-line rounded-xl divide-y divide-layer-line";
const WEG_ZEILE = "p-3 flex items-center gap-x-3";
const WEG_SYMBOL = "shrink-0 size-8 inline-flex justify-center items-center rounded-lg bg-surface text-surface-foreground";
const CHIP = "py-1 ps-1 pe-1 inline-flex items-center gap-x-1.5 text-sm rounded-full bg-layer border border-layer-line text-layer-foreground";
const CHIP_AVATAR = "inline-flex shrink-0 items-center justify-center size-6 rounded-full bg-surface text-xs font-medium text-surface-foreground";

function Inhalt({
  vorbelegt,
  senderName,
  linkUrl,
  linkSatz,
  personenHilfe,
  suche,
  zustand,
  leerFehler,
}: Pick<InformierenDialogProps, "vorbelegt" | "senderName" | "linkUrl" | "linkSatz" | "personenHilfe" | "suche"> & {
  /** Die Auswahl war leer: Satz am Feld (behebbar, r100). */
  leerFehler: boolean;
  zustand: { aktuell: () => { auswahl: InformierenPerson[]; mail: boolean; nachricht: string } | null; setze: (z: { auswahl: InformierenPerson[]; mail: boolean; nachricht: string }) => void };
}) {
  // Der Zustand lebt im Aufrufer des Modals, weil `onBestaetigen` ihn beim Senden braucht (das Modal gibt keine Formularwerte weiter).
  const start = zustand.aktuell();
  const [auswahl, setAuswahl] = useState<InformierenPerson[]>(start?.auswahl ?? vorbelegt);
  const [mail, setMail] = useState(start?.mail ?? true);
  const [nachricht, setNachricht] = useState(start?.nachricht ?? "");
  const [kopiert, setKopiert] = useState<"nein" | "ja" | "fehlt">("nein");
  const timer = useRef<number | null>(null);
  const kopiertId = useId();
  const treffer = useRef(new Map<string, InformierenPerson>());

  useEffect(() => {
    zustand.setze({ auswahl, mail, nachricht });
  }, [auswahl, mail, nachricht, zustand]);
  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  const kopiere = async () => {
    try {
      await navigator.clipboard.writeText(linkUrl);
      setKopiert("ja");
    } catch {
      setKopiert("fehlt");
    }
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setKopiert("nein"), 2500);
  };

  const sucheOhneGewaehlte = async (anfrage: string): Promise<Treffer[]> => {
    const gefunden = await suche(anfrage);
    if (gefunden === null) throw new Error("Suche fehlgeschlagen");
    for (const p of gefunden) treffer.current.set(p.id, p);
    const schon = new Set(auswahl.map((p) => p.id));
    return gefunden.filter((p) => !schon.has(p.id)).map((p) => ({ id: p.id, label: p.name, beschreibung: p.untertitel }));
  };

  return (
    <div className="space-y-5">
      <Feld id="informieren-personen" label="Wen möchtest du informieren?" gruppe hilfe={personenHilfe} fehler={leerFehler && auswahl.length === 0 ? "Bitte mindestens eine Person wählen." : undefined}>
        {() => (
          <div className="space-y-2">
            {auswahl.length > 0 && (
              <ul className="flex flex-wrap gap-2" aria-label="Gewählte Personen">
                {auswahl.map((p) => (
                  <li key={p.id} className={CHIP}>
                    <span className={CHIP_AVATAR} aria-hidden="true">
                      {initialen(p.name)}
                    </span>
                    <span>{p.name}</span>
                    <Knopf variante="ghost" nurSymbol groesse="xs" label={`${p.name} entfernen`} symbol={<EntfernenSymbol />} onClick={() => setAuswahl((a) => a.filter((x) => x.id !== p.id))} />
                  </li>
                ))}
              </ul>
            )}
            <Trefferfeld
              label="Weitere Person suchen"
              labelVersteckt
              platzhalter="Weitere Person suchen"
              startText="Tippe mindestens zwei Buchstaben."
              keineTreffer="Keine passende Person."
              fehlschlagText="Die Suche ist gerade nicht möglich. Bitte erneut versuchen."
              mindestlaenge={2}
              suche={sucheOhneGewaehlte}
              gewaehlt={null}
              beiWahl={(t) => {
                const p = t ? treffer.current.get(t.id) : undefined;
                if (p) setAuswahl((a) => (a.some((x) => x.id === p.id) ? a : [...a, p]));
              }}
              groesse="kompakt"
            />
          </div>
        )}
      </Feld>

      <Feld id="informieren-wege" label="Wege" gruppe hilfe="Wer E-Mails abgeschaltet hat, bekommt nur den Hinweis im Portal.">
        {() => (
          <div className={WEGE_LISTE}>
            {/* Preline: forms/forms-sub-core-inputs-layouts/forms-cat-switches/settings-list-with-switches-and-sorting */}
            <div className={WEG_ZEILE}>
              <span className={WEG_SYMBOL}>
                <GlockeSymbol />
              </span>
              <div className="grow min-w-0">
                <p className="text-sm text-foreground">Hinweis im Portal</p>
                <p className="text-xs text-muted-foreground-1">Erscheint bei der Person unter den Hinweisen.</p>
              </div>
              <Badge ton="gedaempft">Immer</Badge>
            </div>
            <div className={WEG_ZEILE}>
              <span className={WEG_SYMBOL}>
                <MailSymbol />
              </span>
              <div className="grow min-w-0">
                <p className="text-sm text-foreground">E-Mail</p>
                <p className="text-xs text-muted-foreground-1">{`Absender ist das Portal. Antworten gehen an dich (${senderName}).`}</p>
              </div>
              <Schalter layout="zelle" label="E-Mail senden" checked={mail} onChange={(e) => setMail(e.target.checked)} />
            </div>
            <div className={WEG_ZEILE}>
              <span className={WEG_SYMBOL}>
                <PersonenSymbol />
              </span>
              <div className="grow min-w-0">
                <p className="text-sm text-foreground">Teams</p>
                <p className="text-xs text-muted-foreground-1 inline-flex items-center gap-x-1">
                  <SchlossSymbol />
                  Teams-Nachrichten werden eingerichtet.
                </p>
              </div>
              <Schalter layout="zelle" label="Teams (gesperrt)" checked={false} disabled onChange={() => undefined} />
            </div>
          </div>
        )}
      </Feld>

      <Mehrzeiler
        id="informieren-nachricht"
        label="Nachricht"
        optional
        placeholder="Eine Zeile dazu, worum es dir geht"
        rows={3}
        maxLength={NACHRICHT_MAX}
        value={nachricht}
        onChange={(e) => setNachricht(e.target.value)}
      />

      <div className="pt-4 border-t border-layer-line flex flex-wrap items-center gap-x-3 gap-y-1">
        <Knopf variante="sekundaer" groesse="sm" symbol={<KopierSymbol />} onClick={() => void kopiere()} ariaDescribedby={kopiertId}>
          Link kopieren
        </Knopf>
        <p className="grow min-w-0 text-xs text-muted-foreground-1">{linkSatz}</p>
        <span id={kopiertId} role="status" aria-live="polite" className="w-full text-xs text-muted-foreground-1 empty:hidden">
          {kopiert === "ja" && "Link kopiert"}
          {kopiert === "fehlt" && "Kopieren nicht möglich"}
        </span>
      </div>
    </div>
  );
}

/**
 * Der Dialog. Der Zustand (Auswahl, E-Mail-Schalter, Nachricht) liegt in einer Ref, weil das Modal den Vorgang ohne Formularwerte aufruft;
 * er entsteht je Öffnen neu (das Modal hängt seinen Inhalt beim Schließen aus).
 */
export function InformierenDialog(p: InformierenDialogProps) {
  const stand = useRef<{ auswahl: InformierenPerson[]; mail: boolean; nachricht: string } | null>(null);
  const anfrageId = useRef<string | null>(null);
  const [leerFehler, setLeerFehler] = useState(false);

  // Je Öffnen neuer Zustand und neuer Idempotenz-Schlüssel: beim Schließen (auch nach Erfolg) wird beides verworfen.
  const schliesse = () => {
    stand.current = null;
    anfrageId.current = null;
    setLeerFehler(false);
    p.beimSchliessen();
  };

  const zustand = useRef({
    aktuell: () => stand.current,
    setze: (z: { auswahl: InformierenPerson[]; mail: boolean; nachricht: string }) => {
      stand.current = z;
    },
  }).current;

  const sende = async (): Promise<KleinesModalErgebnis> => {
    const z = stand.current ?? { auswahl: p.vorbelegt, mail: true, nachricht: "" };
    if (z.auswahl.length === 0) {
      setLeerFehler(true);
      return { ok: false };
    }
    setLeerFehler(false);
    anfrageId.current ??= crypto.randomUUID();
    const res = await p.senden({ empfaengerIds: z.auswahl.map((x) => x.id), mail: z.mail, nachricht: z.nachricht.trim(), anfrageId: anfrageId.current });
    if (res.ok) {
      p.beimErfolg?.();
      return { ok: true };
    }
    return { ok: false, meldung: res.meldung };
  };

  return (
    <KleinesModal offen={p.offen} groesse="m" titel="Informieren" beschreibung={p.beschreibung} beimSchliessen={schliesse} onBestaetigen={sende} bestaetigenText="Senden">
      <Inhalt vorbelegt={p.vorbelegt} senderName={p.senderName} linkUrl={p.linkUrl} linkSatz={p.linkSatz} personenHilfe={p.personenHilfe} suche={p.suche} zustand={zustand} leerFehler={leerFehler} />
    </KleinesModal>
  );
}
